// MARK://dhk8/admin-shell/pages/DashboardPage
// Landing after login: a tiles overview of the v1 modules + a couple of headline KPIs
// pulled from the reports read-models.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChefHat, LayoutGrid, Users, Printer, FileText, BarChart3, Boxes, Building2, Rocket } from 'lucide-react'
import { useAuthStore } from '../stores/auth-store.js'
import { useTenantStore } from '../stores/tenant-store.js'
import { useAsync } from '../lib/use-async.js'
import { getReportSummary, today } from '../lib/clients/reports.js'
import { copyCatalogFromTestToLive } from '../lib/clients/account.js'
import { mxn } from '../lib/money.js'

const TILES = [
  { to: '/catalog', label: 'Menú / Catálogo', sub: 'Productos, categorías, variantes, combos, precios', Icon: ChefHat },
  { to: '/floor', label: 'Plano', sub: 'Editor del layout de mesas', Icon: LayoutGrid },
  { to: '/users', label: 'Usuarios / Roles', sub: 'Equipo, roles y PINs', Icon: Users },
  { to: '/devices', label: 'Impresoras / KDS', sub: 'Estaciones de impresión y cocina', Icon: Printer },
  { to: '/fiscal', label: 'Ajustes fiscales', sub: 'RFC, CSD, folios, horarios, recibo', Icon: FileText },
  { to: '/reports', label: 'Reportes', sub: 'Ventas, productos, meseros, cortes', Icon: BarChart3 },
  { to: '/invoices', label: 'Facturas / CFDI', sub: 'Timbrado, cancelación, factura global', Icon: FileText },
  { to: '/inventory', label: 'Inventario', sub: 'Existencias y reorden', Icon: Boxes },
]

export default function DashboardPage() {
  const session = useAuthStore((s) => s.session)
  const tenant = useTenantStore((s) => s.tenant)
  const doGoLive = useTenantStore((s) => s.goLive)
  const isSuper = !!session?.roles.includes('superadmin')
  // KPIs de HOY desde el resumen del grafo (erp-reports). Rango = hoy explícito
  // (las tarjetas dicen "de hoy"; sin rango getReportSummary daría histórico).
  // NO se lee el array posicional de un reporte — frágil al layout de columnas.
  const { data: sum } = useAsync((s) => getReportSummary({ from: today(), to: today() }, s))
  const [busy, setBusy] = useState(false)

  async function goLive() {
    if (!confirm('¿Pasar a Modo en vivo?\n\nEl dataset de pruebas se queda intacto para que sigas experimentando. El dataset en vivo es independiente: a partir de ahora, lo que vendas ahí cuenta y se reporta. Podrás alternar entre Pruebas y En vivo con el selector de la barra superior.')) return
    setBusy(true)
    try {
      await doGoLive()
      // First flip to live: offer to seed the live catalog from the test one.
      if (session?.liveTenantId && session?.testTenantId && confirm('¿Copiar tu catálogo de pruebas al dataset En vivo?\n\nAsí no empiezas de cero. (Puedes editarlo o borrarlo después.)')) {
        await copyCatalogFromTestToLive(session.liveTenantId, session.testTenantId)
      }
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Hola, {session?.fullName ?? 'comerciante'}</h1>
          <p className="adm-page__sub">Panel del comerciante · NintaiGO</p>
        </div>
      </div>

      {(session?.activeEnv === 'test' || tenant?.mode === 'SANDBOX') && (
        <div className="adm-banner adm-banner--warn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span><strong>Entorno de pruebas.</strong> Las ventas no cuentan y puedes ajustar todo sin preocuparte. Cuando estés lista para operar de verdad, activa el dataset En vivo.</span>
          <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={goLive} disabled={busy}><Rocket size={15} /> Pasar a Modo en vivo</button>
        </div>
      )}
      {session?.activeEnv === 'live' && tenant?.mode === 'LIVE' && (
        <div className="adm-banner" style={{ background: 'var(--pos-success-bg)', color: 'var(--pos-success)' }}><strong>Modo en vivo.</strong> Las ventas cuentan y se reportan normalmente.</div>
      )}

      <div className="adm-grid">
        <div className="adm-card"><div className="adm-row__meta">Ventas de hoy</div><div className="adm-page__title" style={{ fontSize: '1.5rem' }}>{sum ? mxn(sum.salesCents) : '—'}</div></div>
        <div className="adm-card"><div className="adm-row__meta">Tickets de hoy</div><div className="adm-page__title" style={{ fontSize: '1.5rem' }}>{sum ? String(sum.tickets) : '—'}</div></div>
        <div className="adm-card"><div className="adm-row__meta">Ticket promedio</div><div className="adm-page__title" style={{ fontSize: '1.5rem' }}>{sum ? mxn(sum.avgTicketCents) : '—'}</div></div>
      </div>

      <div className="adm-grid adm-grid--wide">
        {TILES.map(({ to, label, sub, Icon }) => (
          <Link key={to} to={to} className="adm-card" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--pos-radius-md)', background: 'var(--pos-tint-fill)', color: 'var(--pos-tint-on)', flexShrink: 0 }}><Icon size={20} aria-hidden /></span>
            <span><span className="adm-row__title" style={{ display: 'block' }}>{label}</span><span className="adm-row__meta">{sub}</span></span>
          </Link>
        ))}
        {isSuper && (
          <Link to="/superadmin" className="adm-card" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--pos-radius-md)', background: 'var(--pos-warning-bg)', color: 'var(--pos-warning)', flexShrink: 0 }}><Building2 size={20} aria-hidden /></span>
            <span><span className="adm-row__title" style={{ display: 'block' }}>Cuentas de terceros</span><span className="adm-row__meta">Superadmin · tenants, planes, impersonar</span></span>
          </Link>
        )}
      </div>
    </div>
  )
}
